import React, { useState, useEffect, useRef } from 'react';
import { Plus, Loader2, Trash2, Building2, X, Pencil } from 'lucide-react';
import { EmptyState } from './AccountsScreen';
import { useAuth } from '../../context/AuthContext';
import { CURRENCIES, formatUnifiedAmount, getCurrencySymbol } from '../../utils/currency';

const ASSET_CATEGORIES = [
  'Electronics & Gadgets', 'Vehicles', 'Real Estate & Land',
  'Cash & Bank Vaults', 'FX & Domiciliary', 'Crypto & Equities',
];
const MARKET_TRACKED_CATEGORIES = ['Crypto & Equities'];
const LIQUIDITY_LEVELS = ['instant', 'short_term', 'long_term'];

const EMPTY_FORM = {
  name: '', category: ASSET_CATEGORIES[0], estimatedValue: '', currency: 'NGN',
  liquidity: 'short_term', isMarketTracked: false, marketSymbol: '', marketAssetType: 'crypto', quantity: '1',
};

export default function AssetsScreen() {
  const { user } = useAuth();
  const displayCurrency = user?.displayCurrency || 'NGN';

  const [assets, setAssets]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');
  const [editingId, setEditingId] = useState(null);   // assetId being edited, or null when adding
  const formRef = useRef(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch('/api/personal/assets', { credentials: 'include' });
      const data = await res.json();
      if (data.success) setAssets(data.assets);
    } finally {
      setLoading(false);
    }
  }

  const isMarketCategory = MARKET_TRACKED_CATEGORIES.includes(form.category);

  function closeForm() {
    setShowAdd(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  function startAdd() {
    if (showAdd) { closeForm(); return; }
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowAdd(true);
  }

  // Fill the form with an existing asset and switch it to "edit" mode. The
  // category, market symbol and tracking mode are fixed once an asset exists
  // (they decide how it is valued); name, value/quantity, currency (manual
  // assets) and liquidity can be changed.
  function startEdit(a) {
    setEditingId(a.assetId);
    setForm({
      name:            a.name || '',
      category:        ASSET_CATEGORIES.includes(a.category) ? a.category : ASSET_CATEGORIES[0],
      estimatedValue:  a.isMarketTracked ? '' : String(a.estimatedValue ?? ''),
      currency:        a.currency || 'NGN',
      liquidity:       LIQUIDITY_LEVELS.includes(a.liquidity) ? a.liquidity : 'short_term',
      isMarketTracked: Boolean(a.isMarketTracked),
      marketSymbol:    a.marketSymbol || '',
      marketAssetType: a.marketAssetType || 'crypto',
      quantity:        String(a.quantity ?? 1),
    });
    setError('');
    setShowAdd(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const editing = Boolean(editingId);
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (form.isMarketTracked && !editing && !form.marketSymbol.trim()) { setError('Enter a symbol for a market-tracked asset.'); return; }
    if (!form.isMarketTracked && form.estimatedValue === '') { setError('Enter an estimated value.'); return; }
    if (form.isMarketTracked && editing && !(Number(form.quantity) > 0)) { setError('Quantity must be greater than zero.'); return; }

    setSubmitting(true);
    try {
      let body;
      if (editing) {
        body = form.isMarketTracked
          ? { name: form.name, quantity: Number(form.quantity), liquidity: form.liquidity }
          : { name: form.name, estimatedValue: Number(form.estimatedValue), currency: form.currency, liquidity: form.liquidity };
      } else {
        body = {
          ...form,
          estimatedValue: form.estimatedValue ? Number(form.estimatedValue) : undefined,
          quantity: Number(form.quantity) || 1,
          isMarketTracked: isMarketCategory && form.isMarketTracked,
        };
      }
      const res = await fetch(editing ? `/api/personal/assets/${editingId}` : '/api/personal/assets', {
        method: editing ? 'PATCH' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.message || (editing ? 'Could not save changes.' : 'Could not add asset.')); return; }
      closeForm();
      load();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this asset?')) return;
    await fetch(`/api/personal/assets/${id}`, { method: 'DELETE', credentials: 'include' });
    load();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Assets & Portfolio</p>
          <h2 className="text-2xl font-black text-zinc-900">What You Own</h2>
        </div>
        <button onClick={startAdd}
          className="flex items-center gap-1.5 px-4 py-2.5 bg-amber-500 text-black rounded-xl text-xs font-black uppercase tracking-widest hover:bg-amber-400 transition">
          {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? 'Cancel' : 'Add Asset'}
        </button>
      </div>

      {showAdd && (
        <form ref={formRef} onSubmit={handleSubmit} className="bg-white border border-zinc-200 rounded-2xl p-5 mb-6 space-y-3">
          {editingId && (
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Editing asset</p>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <input placeholder="Name (e.g. Toyota Camry 2019)" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500" />
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value, isMarketTracked: false })}
            disabled={Boolean(editingId)} title={editingId ? "An asset's category can't be changed" : undefined}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 disabled:opacity-60">
            {ASSET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {isMarketCategory && !editingId && (
            <label className="flex items-center gap-2 text-sm text-zinc-600">
              <input type="checkbox" checked={form.isMarketTracked}
                onChange={e => setForm({ ...form, isMarketTracked: e.target.checked })} />
              Track this with a live market price (crypto or stock) instead of entering a value manually
            </label>
          )}

          {isMarketCategory && form.isMarketTracked ? (
            <div className="grid grid-cols-2 gap-3">
              <select value={form.marketAssetType} onChange={e => setForm({ ...form, marketAssetType: e.target.value })}
                disabled={Boolean(editingId)}
                className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 disabled:opacity-60">
                <option value="crypto">Crypto</option>
                <option value="stock">Stock</option>
              </select>
              <input placeholder={form.marketAssetType === 'crypto' ? 'CoinGecko id (e.g. bitcoin)' : 'Ticker (e.g. AAPL)'}
                value={form.marketSymbol} onChange={e => setForm({ ...form, marketSymbol: e.target.value })}
                disabled={Boolean(editingId)}
                className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500 disabled:opacity-60" />
              <input type="number" placeholder="Quantity held" value={form.quantity}
                onChange={e => setForm({ ...form, quantity: e.target.value })}
                className="col-span-2 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <input type="number" placeholder="Estimated value" value={form.estimatedValue}
                onChange={e => setForm({ ...form, estimatedValue: e.target.value })}
                className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500" />
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          <select value={form.liquidity} onChange={e => setForm({ ...form, liquidity: e.target.value })}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-500">
            {LIQUIDITY_LEVELS.map(l => <option key={l} value={l}>{l.replace('_', ' ')}</option>)}
          </select>

          {editingId && (
            <p className="text-[11px] text-zinc-500">
              {form.isMarketTracked
                ? 'The value follows the live market price — change the quantity held. The category and symbol are fixed.'
                : 'The category is fixed once an asset is saved.'}
            </p>
          )}
          <button type="submit" disabled={submitting}
            className="w-full py-2.5 bg-zinc-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-zinc-800 transition disabled:opacity-50">
            {submitting ? <Loader2 size={14} className="inline animate-spin" /> : (editingId ? 'Save Changes' : 'Save Asset')}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading...</p>
      ) : assets.length === 0 ? (
        <EmptyState icon={Building2} text="No assets yet. Add a vehicle, property, gadget, or a crypto/stock holding." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {assets.map(a => (
            <div key={a.assetId} className="bg-white border border-zinc-200 rounded-2xl p-4 flex items-start justify-between">
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-zinc-400">{a.category}</p>
                <p className="font-bold text-zinc-900">{a.name}</p>
                {a.isMarketTracked ? (
                  a.priceUnavailable
                    ? <p className="text-sm text-amber-600 mt-1">Price unavailable right now</p>
                    : <p className="text-lg font-black text-zinc-900 mt-1">
                        {formatUnifiedAmount(Math.round(a.estimatedValue || 0), a.currency, displayCurrency)}
                        {a.priceStale && <span className="text-xs text-amber-600 ml-1">(last known)</span>}
                      </p>
                ) : (
                  <p className="text-lg font-black text-zinc-900 mt-1">
                    {formatUnifiedAmount(a.estimatedValue || 0, a.currency, displayCurrency)}
                  </p>
                )}
                {!a.priceUnavailable && a.currency !== displayCurrency && (
                  <p className="text-[10px] font-bold text-zinc-400 mt-0.5">
                    {getCurrencySymbol(a.currency)}{Math.round(a.estimatedValue || 0).toLocaleString()} {a.currency} in native currency
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => startEdit(a)} aria-label="Edit asset" title="Edit"
                  className="text-zinc-300 hover:text-amber-600 transition">
                  <Pencil size={15} />
                </button>
                <button onClick={() => handleDelete(a.assetId)} aria-label="Delete asset" title="Delete"
                  className="text-zinc-300 hover:text-red-500 transition">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
